/**
 * Dashboard (Fase 6): KPIs com comparação ao período anterior, gráfico de gasto × resultados,
 * ranking de campanhas e de criativos e quebras. Uma seção por moeda (moedas nunca se somam).
 * Usado na tela de Relatórios, na Visão geral do Cliente final e na página pública.
 */
import {
  BREAKDOWN_KINDS,
  formatMetric,
  METRIC_DEFS,
  type BreakdownDto,
  type BreakdownKind,
  type DashboardCurrencyDto,
  type DashboardDto,
  type KpiDto,
  type Metrics,
} from "@iagenix/shared";
import { ArrowDownRight, ArrowUpRight, ImageOff, Minus } from "lucide-react";
import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const M = (id: keyof Metrics) => METRIC_DEFS.find((d) => d.id === id) ?? { format: "int" as const };

function fmtDay(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

export function DeltaBadge({ delta }: { delta: KpiDto["delta"] }) {
  if (delta.deltaPct === null) {
    return <span className="text-xs text-muted-foreground">sem base de comparação</span>;
  }
  const Icon = delta.deltaPct > 0 ? ArrowUpRight : delta.deltaPct < 0 ? ArrowDownRight : Minus;
  const tone =
    delta.direction === "melhor"
      ? "text-emerald-700 dark:text-emerald-400"
      : delta.direction === "pior"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-medium", tone)}>
      <Icon className="size-3.5" aria-hidden />
      {delta.deltaPct > 0 ? "+" : ""}
      {delta.deltaPct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
      <span className="font-normal text-muted-foreground"> vs. período anterior</span>
    </span>
  );
}

function KpiCards({ section, accent }: { section: DashboardCurrencyDto; accent?: string }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {section.kpis.map((k) => (
        <Card
          key={k.key}
          className="gap-2 py-4"
          style={accent ? { borderTopColor: accent, borderTopWidth: 3 } : undefined}
        >
          <CardContent className="space-y-1 px-4">
            <p className="text-xs text-muted-foreground">{k.label}</p>
            <p className="text-xl font-semibold tabular-nums">
              {formatMetric(k, k.delta.current, section.currency)}
            </p>
            <DeltaBadge delta={k.delta} />
            <p className="text-[11px] text-muted-foreground">
              antes: {formatMetric(k, k.delta.previous, section.currency)}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SpendResultsChart({
  section,
  accent,
}: {
  section: DashboardCurrencyDto;
  accent?: string;
}) {
  const data = section.timeseries.map((p) => ({
    date: fmtDay(p.date),
    gasto: Math.round(p.spendCents) / 100,
    resultados: p.results,
  }));
  if (data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sem dados diários no período.
      </p>
    );
  }
  const money = (v: number) =>
    formatMetric({ format: "money" }, Math.round(v * 100), section.currency);
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={16} />
          <YAxis
            yAxisId="gasto"
            tick={{ fontSize: 11 }}
            width={70}
            tickFormatter={(v: number) => money(v)}
          />
          <YAxis yAxisId="resultados" orientation="right" tick={{ fontSize: 11 }} width={40} />
          <Tooltip
            formatter={(value, name) =>
              name === "gasto" ? [money(Number(value)), "Gasto"] : [String(value), "Resultados"]
            }
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
          />
          <Legend formatter={(v: string) => (v === "gasto" ? "Gasto" : "Resultados")} />
          <Bar
            yAxisId="gasto"
            dataKey="gasto"
            fill={accent ?? "var(--color-primary)"}
            radius={[3, 3, 0, 0]}
          />
          <Line
            yAxisId="resultados"
            type="monotone"
            dataKey="resultados"
            stroke="var(--color-foreground)"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function CampaignRanking({ section }: { section: DashboardCurrencyDto }) {
  if (section.campaigns.length === 0)
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhuma campanha com gasto no período.
      </p>
    );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Campanha</TableHead>
          <TableHead className="text-right">Gasto</TableHead>
          <TableHead className="text-right">Resultados</TableHead>
          <TableHead className="text-right">Custo/result.</TableHead>
          <TableHead className="text-right">CTR</TableHead>
          <TableHead className="text-right">ROAS</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {section.campaigns.map((c) => (
          <TableRow key={`${c.adAccountMetaId}:${c.metaId}`}>
            <TableCell>
              <p className="max-w-72 truncate font-medium" title={c.name}>
                {c.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {c.objectiveLabel} · {c.accountName}
              </p>
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("spendCents"), c.metrics.spendCents, section.currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("results"), c.metrics.results, section.currency)}
              <span className="block text-[11px] text-muted-foreground">{c.resultTypeLabel}</span>
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(
                M("costPerResultCents"),
                c.metrics.costPerResultCents,
                section.currency,
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("ctr"), c.metrics.ctr, section.currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("roas"), c.metrics.roas, section.currency)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CreativeRanking({ section }: { section: DashboardCurrencyDto }) {
  if (section.creatives.length === 0)
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhum criativo com gasto no período.
      </p>
    );
  return (
    <ul className="divide-y">
      {section.creatives.map((c) => (
        <li
          key={`${c.adAccountMetaId}:${c.creativeMetaId}`}
          className="flex items-center gap-3 py-2"
        >
          {c.thumbnailUrl ? (
            <img
              src={c.thumbnailUrl}
              alt=""
              className="size-12 shrink-0 rounded object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex size-12 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
              <ImageOff className="size-4" aria-hidden />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {c.title ?? c.name ?? `Criativo ${c.creativeMetaId}`}
            </p>
            <p className="text-xs text-muted-foreground">
              {c.adCount} anúncio(s) · CTR {formatMetric(M("ctr"), c.metrics.ctr, section.currency)}{" "}
              · CPC {formatMetric(M("cpcCents"), c.metrics.cpcCents, section.currency)}
            </p>
          </div>
          <div className="text-right text-sm tabular-nums">
            <p className="font-medium">
              {formatMetric(M("spendCents"), c.metrics.spendCents, section.currency)}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatMetric(M("results"), c.metrics.results, section.currency)} result.
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function BreakdownTable({
  breakdown,
  currency,
  accent,
}: {
  breakdown: BreakdownDto;
  currency: string;
  accent?: string;
}) {
  if (breakdown.rows.length === 0)
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Sem dados desta quebra no período.
      </p>
    );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{breakdown.label}</TableHead>
          <TableHead className="w-48">% do gasto</TableHead>
          <TableHead className="text-right">Gasto</TableHead>
          <TableHead className="text-right">Impressões</TableHead>
          <TableHead className="text-right">CTR</TableHead>
          <TableHead className="text-right">Resultados</TableHead>
          <TableHead className="text-right">Custo/result.</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {breakdown.rows.map((r) => (
          <TableRow key={r.key}>
            <TableCell className="font-medium">{r.label}</TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 rounded bg-muted">
                  <div
                    className="h-2 rounded"
                    style={{
                      width: `${Math.min(100, r.spendShare)}%`,
                      backgroundColor: accent ?? "var(--color-primary)",
                    }}
                  />
                </div>
                <span className="w-12 text-right text-xs tabular-nums">
                  {r.spendShare.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
                </span>
              </div>
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("spendCents"), r.metrics.spendCents, currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("impressions"), r.metrics.impressions, currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("ctr"), r.metrics.ctr, currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("results"), r.metrics.results, currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMetric(M("costPerResultCents"), r.metrics.costPerResultCents, currency)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CurrencySection({
  section,
  many,
  accent,
}: {
  section: DashboardCurrencyDto;
  many: boolean;
  accent?: string;
}) {
  const [kind, setKind] = useState<BreakdownKind>("age");
  const breakdown = section.breakdowns.find((b) => b.kind === kind);
  return (
    <section className="space-y-4" aria-label={`Resumo ${section.currency}`}>
      {many ? (
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{section.currency}</Badge>
          <p className="text-sm text-muted-foreground">
            {section.accounts.map((a) => a.name).join(", ")} — moedas diferentes não são somadas.
          </p>
        </div>
      ) : null}
      <KpiCards section={section} {...(accent ? { accent } : {})} />
      {section.resultTypes.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Resultados por tipo:{" "}
          {section.resultTypes
            .map((r) => `${r.label} ${formatMetric(M("results"), r.results, section.currency)}`)
            .join(" · ")}
        </p>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>Gasto × resultados no tempo</CardTitle>
          <CardDescription>
            Barras: gasto por dia. Linha: resultados por dia (no fuso da conta).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SpendResultsChart section={section} {...(accent ? { accent } : {})} />
        </CardContent>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Campanhas com mais investimento</CardTitle>
          </CardHeader>
          <CardContent>
            <CampaignRanking section={section} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Criativos com mais investimento</CardTitle>
            <CardDescription>Somando todos os anúncios que usam cada criativo.</CardDescription>
          </CardHeader>
          <CardContent>
            <CreativeRanking section={section} />
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Quebras</CardTitle>
          <CardDescription>
            Idade, gênero, posicionamento, região e dispositivo (nível da conta).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Tabs value={kind} onValueChange={(v) => setKind(v as BreakdownKind)}>
            <TabsList>
              {BREAKDOWN_KINDS.map((k) => (
                <TabsTrigger key={k} value={k}>
                  {section.breakdowns.find((b) => b.kind === k)?.label ?? k}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {breakdown ? (
            <BreakdownTable
              breakdown={breakdown}
              currency={section.currency}
              {...(accent ? { accent } : {})}
            />
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

export function DashboardView({ dashboard, accent }: { dashboard: DashboardDto; accent?: string }) {
  if (dashboard.byCurrency.length === 0) {
    return (
      <p className="rounded-xl border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
        Nenhuma conta de anúncio neste recorte.
      </p>
    );
  }
  const many = dashboard.byCurrency.length > 1;
  return (
    <div className="space-y-8">
      {dashboard.byCurrency.map((s) => (
        <CurrencySection key={s.currency} section={s} many={many} {...(accent ? { accent } : {})} />
      ))}
    </div>
  );
}
